import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ExternalLink } from "lucide-react";
import { LanguageProvider, useLanguage } from "../contexts/LanguageContext";

function CaseContent() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const items = t("cases.items");
  const caseItem = Array.isArray(items) ? items.find((c) => c.slug === slug) : null;
  const currentIndex = Array.isArray(items) ? items.findIndex((c) => c.slug === slug) : -1;
  const prevCase = currentIndex > 0 ? items[currentIndex - 1] : null;
  const nextCase = currentIndex < items.length - 1 ? items[currentIndex + 1] : null;

  if (!caseItem) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <p className="text-neutral-500 mb-4">{t("caseDetail.notFound")}</p>
          <button onClick={() => navigate("/")} className="text-[#00F0FF] hover:underline text-sm">{t("caseDetail.backHome")}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="case-detail-page">
      {/* Sticky nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-outfit font-medium text-white text-lg tracking-tight">
            <span className="w-7 h-7 bg-gradient-to-br from-[#00F0FF] to-[#0080FF] rounded-sm flex items-center justify-center text-black font-bold text-xs">w</span>
            wedowedo
          </Link>
          <button
            onClick={() => { navigate("/"); setTimeout(() => { document.getElementById("cases")?.scrollIntoView({ behavior: "smooth" }); }, 300); }}
            data-testid="back-to-cases-btn"
            className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> {t("caseDetail.back")}
          </button>
        </div>
      </nav>

      {/* Hero image */}
      {caseItem.image && (
        <div className="relative w-full h-[50vh] md:h-[60vh] overflow-hidden">
          <img
            src={caseItem.image}
            alt={caseItem.client}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-[#050505]/20" />
          <div className="absolute bottom-0 left-0 right-0 px-6 md:px-12 pb-12">
            <div className="max-w-[1200px] mx-auto">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                <span className="font-mono text-xs tracking-[0.25em] text-[#00F0FF] mb-3 block">{caseItem.industry}</span>
                <h1 className="font-outfit font-bold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tighter">{caseItem.client}</h1>
              </motion.div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-16 md:py-24">
        {/* Challenge & Solution side by side */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 mb-16 md:mb-24"
        >
          <div>
            <span className="font-mono text-[10px] tracking-[0.2em] text-neutral-600 block mb-3">CHALLENGE</span>
            <p className="text-base text-neutral-300 font-manrope leading-relaxed">{caseItem.challenge}</p>
          </div>
          <div>
            <span className="font-mono text-[10px] tracking-[0.2em] text-neutral-600 block mb-3">SOLUTION</span>
            <p className="text-base text-neutral-300 font-manrope leading-relaxed">{caseItem.solution}</p>
          </div>
        </motion.div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-16 md:mb-24"
        >
          <span className="font-mono text-[10px] tracking-[0.2em] text-neutral-600 block mb-6">WHAT WE BUILT</span>
          <div className="flex flex-wrap gap-2">
            {caseItem.features?.map((f, i) => (
              <span
                key={i}
                className="text-sm font-mono tracking-wider text-neutral-300 bg-white/5 px-4 py-2 rounded-sm border border-white/10"
              >
                {f}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Extended description */}
        {caseItem.description?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mb-16 md:mb-24"
          >
            <div className="max-w-3xl">
              {caseItem.description.map((para, i) => (
                <p key={i} className="text-base text-neutral-400 font-manrope leading-relaxed mb-6 last:mb-0">
                  {para}
                </p>
              ))}
            </div>
          </motion.div>
        )}

        {/* Result highlight */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="border-l-2 border-[#00F0FF] pl-6 md:pl-8 mb-16 md:mb-24"
        >
          <span className="font-mono text-[10px] tracking-[0.2em] text-neutral-600 block mb-3">RESULT</span>
          <p className="text-lg md:text-xl text-[#00F0FF] font-manrope font-medium leading-relaxed">{caseItem.result}</p>
        </motion.div>

        {/* Navigation between cases */}
        <div className="flex flex-col sm:flex-row items-stretch gap-4 border-t border-white/5 pt-12">
          {prevCase ? (
            <Link
              to={`/cases/${prevCase.slug}`}
              data-testid="prev-case-link"
              className="flex-1 group bg-[#0a0a0a] border border-white/5 rounded-sm p-6 hover:border-white/15 transition-all"
            >
              <span className="font-mono text-[10px] tracking-[0.2em] text-neutral-600 flex items-center gap-2 mb-2">
                <ArrowLeft className="w-3 h-3" /> {t("caseDetail.previous")}
              </span>
              <span className="text-white font-outfit font-medium group-hover:text-[#00F0FF] transition-colors">{prevCase.client}</span>
            </Link>
          ) : <div className="flex-1" />}
          {nextCase ? (
            <Link
              to={`/cases/${nextCase.slug}`}
              data-testid="next-case-link"
              className="flex-1 group bg-[#0a0a0a] border border-white/5 rounded-sm p-6 hover:border-white/15 transition-all text-right"
            >
              <span className="font-mono text-[10px] tracking-[0.2em] text-neutral-600 flex items-center gap-2 justify-end mb-2">
                {t("caseDetail.next")} <ArrowRight className="w-3 h-3" />
              </span>
              <span className="text-white font-outfit font-medium group-hover:text-[#00F0FF] transition-colors">{nextCase.client}</span>
            </Link>
          ) : <div className="flex-1" />}
        </div>

        {/* CTA */}
        <div className="text-center mt-16 md:mt-24">
          <p className="text-neutral-500 font-manrope mb-6">{t("caseDetail.cta")}</p>
          <Link
            to="/onboarding"
            data-testid="case-cta-btn"
            className="inline-flex items-center gap-2 bg-[#00F0FF] text-black px-8 py-3 rounded-sm font-outfit font-medium hover:shadow-[0_0_20px_rgba(0,240,255,0.3)] transition-all"
          >
            {t("caseDetail.ctaBtn")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function CaseDetailPage() {
  return (
    <LanguageProvider>
      <CaseContent />
    </LanguageProvider>
  );
}
